// src/services/actionSheets.js
// -----------------------------------------------------------------------------
// Pure Action Sheet helpers, extracted from ShelterRequestView so BOTH the
// shelter read view and the responder view can render the same per-session
// action sheets (the dated buttons + the modal).
//
//   collectSessionDates(children)                → sorted unique confirmed-session dates
//   getSessionItems(child, sessionDate, opts)    → the items confirmed in one session
//   itemOutcomeMessage(status, reason)           → the Declined / More-Info wording (db-148)
//
// Kept framework-free (no React) so it can be unit-tested directly.

import { supplierMeta } from "../hooks/useUniformSuppliers";
import { APPROVAL_STATUSES } from "../utils/enums";
import { calcUniItems } from "../utils/formConstants";
import { deriveSupplierType } from "./approvals";

// Every unique confirmed-session date across all children, oldest first.
export function collectSessionDates(children) {
  const dates = [];
  (children || []).forEach(function (child) {
    (child.fees || []).forEach(function (f) {
      if (f.confirmedAt && dates.indexOf(f.confirmedAt) < 0) dates.push(f.confirmedAt);
    });
    if (child.uniforms?.confirmedAt && dates.indexOf(child.uniforms.confirmedAt) < 0)
      dates.push(child.uniforms.confirmedAt);
    if (child.shoes?.confirmedAt && dates.indexOf(child.shoes.confirmedAt) < 0)
      dates.push(child.shoes.confirmedAt);
    if (child.laptop?.confirmedAt && dates.indexOf(child.laptop.confirmedAt) < 0)
      dates.push(child.laptop.confirmedAt);
  });
  dates.sort();
  return dates;
}

// The items a child had confirmed in one specific session.
// opts = { reqState, uniformSuppliers } — reqState stamps each item; uniformSuppliers
// (the admin supplier list) derives the Uniform item's supplierType for next-steps.
export function getSessionItems(child, sessionDate, opts) {
  const options = opts || {};
  const reqState = options.reqState || "";
  const uniformSuppliers = options.uniformSuppliers;
  const items = [];
  if (!child) return items;

  (child.fees || []).forEach(function (f) {
    if (f.confirmedAt === sessionDate)
      items.push({
        type: f.feeType || "School Costs",
        status: f.approvalStatus,
        amount: f.feeAmount,
        category: "fees",
        feeType: f.feeType || "",
        reason: f.reason || "",
        state: reqState,
      });
  });

  if (child.uniforms && child.uniforms.confirmedAt === sessionDate) {
    const uSup = child.uniforms.supplier || "";
    // supplierType drives the shelter's Action Sheet next-steps. Since db-50 it is
    // DERIVED from the supplier ROW (payment_method x document_type, honouring the
    // admin's action_sheet_override) via deriveSupplierType — no longer hardcoded
    // name lists. Unknown / not-yet-loaded supplier → "direct".
    const uMeta = supplierMeta(uniformSuppliers, uSup);
    const uSupType = deriveSupplierType({
      kind: uMeta.kind,
      payment_method: uMeta.paymentMethod,
      document_type: uMeta.documentType,
      action_sheet_override: uMeta.actionSheetOverride,
    });
    const uAmt = calcUniItems([child.uniforms]);
    items.push({
      type: `Uniforms${uSup ? ` — ${uSup}` : ""}`,
      status: child.uniforms.approvalStatus,
      amount: uAmt > 0 ? uAmt : null,
      category: "uniforms",
      supplier: uSup,
      supplierType: uSupType,
      reason: child.uniforms.reason || "",
      state: reqState,
    });
  }

  if (child.shoes && child.shoes.confirmedAt === sessionDate) {
    const shoeAmt =
      (child.shoes.jQty || 0) * (parseFloat(child.shoes.jCost) || 0) +
      (child.shoes.lQty || 0) * (parseFloat(child.shoes.lCost) || 0);
    items.push({
      type: "Shoes",
      status: child.shoes.approvalStatus,
      amount: shoeAmt > 0 ? shoeAmt : null,
      category: "shoes",
      reason: child.shoes.reason || "",
      state: reqState,
    });
  }

  if (child.laptop && child.laptop.confirmedAt === sessionDate) {
    items.push({
      type: `Device${child.laptop.deviceType ? ` — ${child.laptop.deviceType}` : ""}`,
      status: child.laptop.approvalStatus,
      amount: child.laptop.estimatedCost || null,
      category: "device",
      reason: child.laptop.reason || "",
      state: reqState,
    });
  }

  return items;
}

// The outcome message shown on the read-view detail popup + the printed Action
// Sheet for a NON-approved item (db-148). When the responder recorded a reason,
// show "REASON: <reason>"; otherwise fall back to the old "see Comments" line.
// Approved items don't come here (they render the next-steps template).
export function itemOutcomeMessage(status, reason) {
  const r = String(reason || "").trim();
  if (status === APPROVAL_STATUSES.DECLINED) {
    return r
      ? `REASON: ${r}`
      : "This item was not approved. Please see the Comments section for details.";
  }
  if (status === APPROVAL_STATUSES.NEEDS_INFO) {
    return r
      ? `MORE INFORMATION NEEDED — REASON: ${r}`
      : "More information has been requested. Please see the Comments section for details.";
  }
  return "This item is still pending a decision.";
}
